import { useState, useCallback, useRef, useEffect } from 'react';
import { Node, Edge } from 'reactflow';

interface HistoryState {
  nodes: Node[];
  edges: Edge[];
}

export const useHistory = (initialNodes: Node[] = [], initialEdges: Edge[] = []) => {
  const [history, setHistory] = useState<HistoryState[]>([{ nodes: initialNodes, edges: initialEdges }]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Use refs to always have the latest values
  const historyRef = useRef(history);
  const currentIndexRef = useRef(currentIndex);

  // Keep refs in sync with state
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const takeSnapshot = useCallback((nodes: Node[], edges: Edge[]) => {
    setCurrentIndex(prevIndex => {
      setHistory(prevHistory => {
        const newHistory = prevHistory.slice(0, prevIndex + 1);
        newHistory.push({ nodes, edges });
        // Limit history to 50 steps
        if (newHistory.length > 50) {
          newHistory.shift();
          return newHistory;
        }
        return newHistory;
      });
      const newHistoryLength = Math.min(prevIndex + 2, 50);
      return newHistoryLength - 1;
    });
  }, []);

  const undo = useCallback(() => {
    const idx = currentIndexRef.current;
    const hist = historyRef.current;

    if (idx > 0) {
      const newIndex = idx - 1;
      setCurrentIndex(newIndex);
      return hist[newIndex];
    }
    return null;
  }, []);

  const redo = useCallback(() => {
    const idx = currentIndexRef.current;
    const hist = historyRef.current;

    if (idx < hist.length - 1) {
      const newIndex = idx + 1;
      setCurrentIndex(newIndex);
      return hist[newIndex];
    }
    return null;
  }, []);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;

  return {
    takeSnapshot,
    undo,
    redo,
    canUndo,
    canRedo,
  };
};
